import { useEffect, useState } from 'react'
import type { CheckboxChangeEvent } from 'antd/es/checkbox'

interface UseCheckboxHandlerProps {
  completed: boolean
  onCompleteToggle: (isCompleted: boolean) => void
}

interface UseCheckboxHandlerReturn {
  localCompleted: boolean
  handleCheckboxChange: (e: CheckboxChangeEvent) => void
}

/**
 * 체크박스 완료 상태 처리를 위한 커스텀 훅
 * 낙관적 UI 업데이트(Optimistic UI)를 제공하여 즉각적인 사용자 경험 제공
 */
export const useCheckboxHandler = ({
  completed,
  onCompleteToggle,
}: UseCheckboxHandlerProps): UseCheckboxHandlerReturn => {
  const [localCompleted, setLocalCompleted] = useState<boolean>(completed)
  const [pendingToggle, setPendingToggle] = useState<boolean | null>(null)

  // props의 completed 값이 변경될 때 로컬 상태 동기화
  // IMPORTANT: 사용자 액션 중이 아닐 때만 서버 값과 동기화
  useEffect(() => {
    // pending toggle이 없을 때만 prop 값으로 동기화
    // 사용자가 클릭 중일 때는 로컬 상태 유지 (즉각적인 UI 피드백)
    if (pendingToggle === null) {
      setLocalCompleted(completed)
    }
  }, [completed, pendingToggle])

  // 지연된 완료 토글 처리
  useEffect(() => {
    if (pendingToggle !== null) {
      const timer = setTimeout(() => {
        onCompleteToggle(pendingToggle)
        setPendingToggle(null)
      }, 100) // 100ms 지연

      return () => clearTimeout(timer)
    }
  }, [pendingToggle, onCompleteToggle])

  const handleCheckboxChange = (e: CheckboxChangeEvent): void => {
    e.stopPropagation()
    const isChecked = e.target.checked

    // 즉시 로컬 상태 업데이트 (UI 변경)
    setLocalCompleted(isChecked)

    // 지연된 완료 토글 처리 설정
    setPendingToggle(isChecked)
  }

  return {
    localCompleted,
    handleCheckboxChange,
  }
}
